import { Select } from 'shared/form/components/Select';
import { SelectOption } from 'shared/form/models/SelectOption';
import { Tarjeta } from 'core/tarjeta/models/Tarjeta';
import { useEffect, useMemo } from 'react';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { RegisterOptions, useController } from 'react-hook-form';
import { InputPlaceholder } from 'shared/placeholders';
import { SearchTarjetaRequest } from 'core/tarjeta/models/SearchTarjetaRequest';

interface TarjetaSelectorProps {
  filtros: SearchTarjetaRequest;
  name: string;
  registerOptions?: RegisterOptions<any, any>;
  reloadToken?: number;
}

/**
 * Componente selector de Tarjetas asíncrono.
 *
 * Actúa como un **Adaptador** entre la UI y el Formulario:
 * - **UI (Visual):** Muestra el objeto completo `{ label, value }` para que el usuario vea el nombre/número.
 * - **Formulario (Datos):** Guarda únicamente el `ID` (number) de la tarjeta seleccionada.
 *
 * @component
 * @example
 * // Uso básico dentro de un formulario
 * <TarjetaSelector
 * name="tarjetaId"
 * cuenta={cuentaSeleccionada} // Objeto cuenta del cual filtrar las tarjetas
 * registerOptions={{ required: "Este campo es obligatorio" }}
 * />
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Cuenta} props.cuenta - Objeto Cuenta del cual se obtendrán las tarjetas asociadas. Si es `null`, no se realiza la búsqueda.
 * @param {string} props.name - Nombre del campo en el formulario (key en `data`).
 * @param {RegisterOptions} [props.registerOptions] - Reglas de validación de `react-hook-form` (ej. required, min, etc.).
 *
 * @returns {JSX.Element} Un componente Select controlado o un Placeholder de carga.
 */
export const TarjetaSelector = ({
  filtros,
  name,
  registerOptions,
  reloadToken = 0,
}: TarjetaSelectorProps): JSX.Element => {
  const [tarjetas, setRequest] = useDataFetcher<SelectOption<Tarjeta>[]>();
  const coreService = useCoreService();
  const {
    field: { value, onChange },
    fieldState: { invalid },
    formState: { isSubmitted },
  } = useController({
    name,
    rules: registerOptions,
    defaultValue: null,
  });

  const filtrosEstables = useMemo(() => {
    return filtros;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filtros)]);

  useEffect(() => {
    setRequest(async () => {
      const response = await coreService.getAllTarjetas(filtrosEstables || {});
      return response.content?.map((tarjeta) => ({
        label: `${tarjeta.numero} - ${tarjeta.denominacion}`,
        value: tarjeta,
      }));
    });
  }, [coreService, setRequest, filtrosEstables, reloadToken]);

  const tarjetasOptions = useMemo(
    () => [{ value: null, label: 'Todas las Tarjetas' }, ...(tarjetas?.data || [])],
    [tarjetas?.data],
  );

  const selectedOption = useMemo(() => {
    return (
      tarjetasOptions.find((option) => {
        if (!option.value) {
          return option.value === value;
        }
        return option.value.id === value;
      }) || null
    );
  }, [tarjetasOptions, value]);

  const handleSelection = (option: SelectOption<Tarjeta>) => {
    onChange(option?.value?.id || null);
  };

  if (tarjetas.isLoading) {
    return (
      <div className="mt-3 py-1">
        <InputPlaceholder />
      </div>
    );
  }

  return (
    <Select
      name="tarjeta"
      label="Tarjeta"
      options={tarjetasOptions}
      value={selectedOption}
      onChange={handleSelection}
      classNames={{
        control: () => {
          if (!isSubmitted) {
            return '';
          }
          return invalid ? 'state-error' : 'state-valid';
        },
      }}
    />
  );
};
